export const SALVAGE_TITLE = 'SALVAGE_TITLE';

export const NO_SALVAGE_TITLE = 'NO_SALVAGE_TITLE';

export const ONE_OWNER = 'ONE_OWNER';

export const NO_ONE_OWNER = 'NO_ONE_OWNER';

export const ACCIDENTS_REPORTED = 'ACCIDENTS_REPORTED';

export const NO_ACCIDENTS_REPORTED = 'NO_ACCIDENTS_REPORTED';

export const OPEN_RECALLS_REPORTED = 'OPEN_RECALLS_REPORTED';

export const NO_OPEN_RECALLS_REPORTED = 'NO_OPEN_RECALLS_REPORTED';

/**
 * Array of all valid VHR overview item types that can be displayed in the overview.
 * This serves as a whitelist for filtering VHR data.
 *
 * @constant {string[]}
 */
export const vhrOverviewWhitelist = [
    SALVAGE_TITLE,
    NO_SALVAGE_TITLE,
    ONE_OWNER,
    NO_ONE_OWNER,
    ACCIDENTS_REPORTED,
    NO_ACCIDENTS_REPORTED,
    OPEN_RECALLS_REPORTED,
    NO_OPEN_RECALLS_REPORTED,
];

/**
 * Generates safety recall labels based on recall count data.
 *
 * This function processes safety recall information and returns appropriate VHR labels
 * based on the recall count. It serves as the primary source of truth for recall
 * labeling when VHR preview labels are not available from the API.
 *
 * The function handles various edge cases including invalid counts, missing data,
 * and ensures that only valid recall information is used for labeling.
 *
 * @param {Object} safetyRecall - Safety recall data object
 * @param {string} safetyRecall.count - The number of recalls as a string
 * @returns {string[]} Array of recall labels, empty array if data is invalid
 */
export const getSafetyRecallLabel = (safetyRecall) => {
    // This is going to be the primary source of truth for recalls for now as these VHR preview labels are not sent
    // If the business logic changes in the future, we can essentially just remove the `recallLabelByCount` and use the `labels` array directly
    const parsedCount = parseInt(safetyRecall?.count, 10);
    const isInvalidCount = Number.isNaN(parsedCount) || parsedCount < 0;

    // If there is no `safetyRecall` object or if the count property is invalid, we cannot assume the number of recalls, so we don't include the recall label
    if (isInvalidCount) return [];

    const recallLabelByCount = parsedCount > 0 ? [OPEN_RECALLS_REPORTED] : [NO_OPEN_RECALLS_REPORTED];
    return recallLabelByCount;
};

/**
 * Filters a VHR preview array to include only valid overview items and merges safety recall data.
 *
 * This function takes an array of VHR preview items and optional safety recall data,
 * then returns a filtered array containing only the items that are defined in the
 * vhrOverviewWhitelist. It also generates and includes appropriate safety recall labels
 * based on the recall count data.
 *
 * The function ensures that only recognized and supported VHR data types are displayed
 * in the vehicle history overview section, removes duplicates, and combines preview
 * data with dynamically generated recall labels.
 *
 * @param {string[]} vhrPreview - Array of VHR preview item strings to filter
 * @param {Object} [safetyRecall] - Optional safety recall data object
 * @param {string} [safetyRecall.count] - The number of recalls as a string
 * @returns {string[]} Filtered array containing only valid and unique VHR overview items
 */
export default function getVhrOverviewList(vhrPreview, safetyRecall) {
    if (!Array.isArray(vhrPreview)) return [];

    const safetyRecallLabels = getSafetyRecallLabel(safetyRecall);
    const filteredList = [...vhrPreview, ...safetyRecallLabels].filter((label) => vhrOverviewWhitelist.includes(label));

    // Use Set to remove duplicates and return a unique list
    return Array.from(new Set(filteredList));
}
